import { BloodPressureObservation } from "@/model/BloodPressureObservation";
import { BloodPressureReading } from "@/model/BloodPressureReading";
import cn from "@/util/cn";
import { Instant, LocalDate, ZoneId } from "@js-joda/core";
import { ComponentPropsWithoutRef } from "react";
import { HeaderCell } from "../Cell";
import BloodPressuresCell from "./BloodPressuresCell";

type BloodPressuresTableProps = ComponentPropsWithoutRef<"div"> & {
  dates: LocalDate[];
  observations: BloodPressureObservation[];
  lastReviewTime: Instant;
};

const BloodPressuresTable = ({
  dates,
  observations,
  lastReviewTime,
  className,
  ...otherProps
}: BloodPressuresTableProps) => {
  const readings = observations.map((observation) => observation.reading);
  return (
    <div className={cn("ps-px", className)} {...otherProps}>
      <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1fr] gap-px">
        <WeekdayHeaderCell>Sun</WeekdayHeaderCell>
        <WeekdayHeaderCell>Mon</WeekdayHeaderCell>
        <WeekdayHeaderCell>Tue</WeekdayHeaderCell>
        <WeekdayHeaderCell>Wed</WeekdayHeaderCell>
        <WeekdayHeaderCell>Thu</WeekdayHeaderCell>
        <WeekdayHeaderCell>Fri</WeekdayHeaderCell>
        <WeekdayHeaderCell>Sat</WeekdayHeaderCell>
        {dates.map((date) => {
          const _readings = readingsForCell({ readings, date });
          const startInstant = date.atStartOfDay().atZone(ZoneId.SYSTEM).toInstant();
          const endInstant = date.plusDays(1).atStartOfDay().atZone(ZoneId.SYSTEM).toInstant();
          const isNew =
            (startInstant.isAfter(lastReviewTime) && endInstant.isBefore(Instant.now())) ||
            _readings.some((reading) => reading.dateTime.toInstant().isAfter(lastReviewTime));
          return (
            <BloodPressuresCell
              key={`${date.toString()}`}
              readings={_readings}
              date={date}
              status={isNew ? "new" : "default"}
              className="p-2"
            />
          );
        })}
      </div>
    </div>
  );
};

const WeekdayHeaderCell = ({
  className,
  ...otherProps
}: ComponentPropsWithoutRef<typeof HeaderCell>) => (
  <HeaderCell
    outline={false}
    className={cn("justify-end text-lg text-primary/90", className)}
    {...otherProps}
  />
);

const readingsForCell = ({
  readings,
  date,
}: {
  readings: BloodPressureReading[];
  date: LocalDate;
}): BloodPressureReading[] =>
  readings.filter((reading) => reading.dateTime.toLocalDate().equals(date));

export default BloodPressuresTable;
